import {
  ForwardedRef,
  forwardRef,
  ReactElement,
  RefObject,
  useMemo,
} from 'react'
import {
  GalleryDetailsViewProps,
} from './GalleryDetails.types'
import Styles from './GalleryDetails.module.scss'
import GalleryCard from 'openspace/components/GalleryCard'
import Container from 'openspace/components/Container'
import DropItem from 'openspace/components/DropItem'
import { t, tO } from 'translate/i18n'
import Section from 'openspace/components/Section'
import { BaseExpandedWidth } from 'openspace/components/DropItem/DropItem.view'
import BackButton from 'components/BackButton'
import Text from '@onepercentio/one-ui/components/Text'
import { FigmaTypo } from 'containers/OneUIProvider/OneUIProvider'
import { DropItemViewMode } from 'openspace/components/DropItem/DropItem.types'
import NoOffersAvailable from './NoOffersAvailable/NoOffersAvailable'
import { useFeatures } from 'context/Feature'
import ChallengeSection from 'features/Challenge/ChallengeSection'
import { BACK_BUTTON_TEST_ID } from 'components/BackButton/BackButton.e2e'

const AssetItemWidth = BaseExpandedWidth + 20

const Empty = () => (
  <div className={Styles.noItems}>
    <Text type={FigmaTypo.H3}>{tO('generic.noItemsFound')}</Text>
  </div>
)

/**
 * Shows the information and drops from a gallery
 **/
function GalleryDetailsView(
  {
    gallery,
    drops,
    expiredDrops,
    marketplaceAssets,
    onGoBack,
    onSeeAll,
    dropGroups,
  }: GalleryDetailsViewProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { challenges } = useFeatures()
  const dropGroupsViews = useMemo<ReactElement[]>(() => {
    return Object.keys(dropGroups).map((group) => (
      <Section
        key={`group-${group}`}
        jsx={{
          empty: null,
          loading: null,
          baseWidth: AssetItemWidth,
          factory: (a) => (
            <DropItem
              availability='open'
              dropOrAsset={a}
              mode={DropItemViewMode.EXPANDED}
              heroSuffix={group}
            />
          ),
        }}
        src={dropGroups[group]}
        title={group}
      />
    ))
  }, [dropGroups])

  return (
    <>
      <div>
        <GalleryCard
          gallery={gallery}
          shouldExpandOnMount={false}
          mode={gallery.highlight ? 'highlight' : 'default'}
          banner={true}
        />
        <Container className={Styles.back}>
          <BackButton
            onClick={onGoBack}
            text={t('authenticated.payment.goBack')}
            data-testid={BACK_BUTTON_TEST_ID}
          />
        </Container>
      </div>
      <Container>
        <div className={Styles.container}>
          {challenges && (
            <ChallengeSection key='challenges' galleryId={gallery.id} />
          )}
          {dropGroupsViews}
          <Section
            key={'a'}
            jsx={{
              empty: <NoOffersAvailable />,
              loading: null,
              baseWidth: AssetItemWidth,
              factory: (a) => (
                <DropItem
                  availability='open'
                  dropOrAsset={a}
                  mode={DropItemViewMode.EXPANDED}
                />
              ),
            }}
            src={drops}
            title={tO('galleryDetails.items')}
            ref={ref as RefObject<HTMLDivElement>}
          />
          {/* Tenant oken: Marketplace assets section ('Itens de emissores') removed */}
          {(expiredDrops.totalItems() || 0) > 0 && (
            <Section
              key={'e'}
              jsx={{
                empty: <Empty />,
                loading: null,
                baseWidth: AssetItemWidth,
                factory: (a) => (
                  <DropItem
                    availability='expired'
                    dropOrAsset={a}
                    mode={DropItemViewMode.EXPANDED}
                  />
                ),
              }}
              src={expiredDrops}
              title={tO('galleryDetails.expiredItems')}
            />
          )}
        </div>
        <br />
        <br />
      </Container>
    </>
  )
}

export default forwardRef(GalleryDetailsView)
